import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";
import LegalPageShell, { LegalSection } from "@/components/legal/LegalPageShell";

const Disclaimer = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <PageMeta
      title="Disclaimer — GoToCoins"
      description="GoToCoins publishes general information and AI-generated crypto research for Australian traders. Not personalised financial advice. Read the full disclaimer."
      path="/disclaimer"
    />
    <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      <LegalPageShell
        icon={ShieldAlert}
        title="Disclaimer"
        subtitle="The single, formal version of the 'educational only, not financial advice' notice used across GoToCoins."
        lastUpdated="26 August 2026"
      >
        <Card className="border-warning/30 bg-warning/5 mb-8">
          <CardContent className="py-4">
            <p className="text-sm">
              <strong>GoToCoins does not provide financial product advice.</strong> Nothing on this
              site is a recommendation to buy, sell, or hold any cryptocurrency, and nothing here
              takes into account your objectives, financial situation, or needs.
            </p>
          </CardContent>
        </Card>

        <LegalSection title="1. General information only">
          <p>
            All content on GoToCoins — including TOSS scores, GEM scores, the AI Coin Reviewer,
            Daily Picks, Undervalued Gems, market phase reports, the Track Record ledger, articles
            and chat responses from GoToBuddy — is general information and educational commentary
            only. It is not personal advice and is not a financial product recommendation under the
            <em> Corporations Act 2001</em> (Cth).
          </p>
          <p>
            GoToCoins does not hold an Australian Financial Services Licence (AFSL) and is not
            authorised to provide personal financial product advice. Before acting on anything you
            read here, consider whether it is appropriate for you and seek advice from a licensed
            financial adviser and a registered tax agent.
          </p>
        </LegalSection>

        <LegalSection title="2. AI-generated content">
          <p>
            Substantial portions of this site are generated by large language models working from
            market data. AI output can be incomplete, out of date, or wrong, including confidently
            wrong. Content marked with the "AI Generated" badge has not been reviewed by a human
            unless it is also marked "Human reviewed".
          </p>
          <p>
            Scores, rankings and narrative summaries are model outputs, not statements of fact about
            a project's quality or prospects. Always verify numbers against a primary source before
            relying on them.
          </p>
        </LegalSection>

        <LegalSection title="3. Market data accuracy">
          <p>
            Prices, market caps, volumes and technical indicators are sourced from third-party
            providers (including CoinMarketCap and CoinGecko) and may be delayed, cached, or
            incorrect. AUD figures are converted from USD using third-party FX rates. GoToCoins does
            not warrant the accuracy, completeness or timeliness of any figure shown.
          </p>
        </LegalSection>

        <LegalSection title="4. Past performance and track record">
          <p>
            The Track Record page records published picks and their outcomes against a fixed
            resolution rule. It is a historical log, not a prediction, and past performance is not a
            reliable indicator of future performance. Recorded outcomes do not represent the returns
            of any actual portfolio, and do not account for fees, slippage, spreads, or tax.
          </p>
        </LegalSection>

        <LegalSection title="5. Crypto risk">
          <p>
            Cryptoassets are highly volatile and largely unregulated. You can lose your entire
            investment. Micro-cap and low-liquidity tokens carry additional risks including
            manipulation, sudden delisting, and total loss of value. Never commit funds you cannot
            afford to lose entirely.
          </p>
        </LegalSection>

        <LegalSection title="6. Affiliate relationships">
          <p>
            GoToCoins has affiliate relationships with Swyftx, CoinSpot, CoinJar and other exchanges
            listed on this site. We may receive a commission or referral fee if you sign up or trade
            through our links, at no additional cost to you. These arrangements do not affect TOSS
            or GEM scoring, which is computed from market data before any commercial consideration.
          </p>
        </LegalSection>

        <LegalSection title="7. Exchange connections">
          <p>
            The optional Swyftx portfolio connection is read-only and is provided for convenience.
            GoToCoins is not affiliated with, endorsed by, or acting as an agent of Swyftx or any
            other exchange, and is not responsible for their platforms, fees, outages or decisions.
          </p>
        </LegalSection>

        <LegalSection title="8. No liability">
          <p>
            To the maximum extent permitted by law, GoToCoins and its operator exclude all liability
            for any loss or damage arising from use of, or reliance on, this site. Nothing in this
            disclaimer excludes rights you have under the Australian Consumer Law that cannot
            lawfully be excluded.
          </p>
        </LegalSection>

        <p className="text-sm text-muted-foreground mt-8">
          See also the <Link to="/terms" className="underline underline-offset-2">Terms of Use</Link>{" "}
          and <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
          Questions? <Link to="/contact" className="underline underline-offset-2">Contact us</Link>.
        </p>
      </LegalPageShell>
    </main>
    <SiteFooter />
  </div>
);

export default Disclaimer;
